import { ApiError, TimeoutError } from './api';

export interface RetryConfig {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Initial delay in ms before first retry (default: 1000) */
  initialDelay?: number;
  /** Multiplier for exponential backoff (default: 2) */
  backoffMultiplier?: number;
  /** Maximum delay between retries in ms (default: 8000) */
  maxDelay?: number;
}

const DEFAULT_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  initialDelay: 1000,
  backoffMultiplier: 2,
  maxDelay: 8000,
};

/**
 * Determine if an error should trigger a retry.
 * Retry: TimeoutError, network errors, 5xx server errors
 * No retry: 4xx client errors (bad request, not found, etc.)
 */
export function isRetryableError(error: unknown): boolean {
  // Timeout errors are retryable
  if (error instanceof TimeoutError) {
    return true;
  }

  // API errors: retry 5xx, don't retry 4xx
  if (error instanceof ApiError) {
    return error.status >= 500;
  }

  // Network errors (TypeError: Failed to fetch, etc.) are retryable
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // Generic network errors
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') ||
      message.includes('failed to fetch') ||
      message.includes('connection') ||
      message.includes('timeout')
    );
  }

  return false;
}

/**
 * Wait for a specified number of milliseconds.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Calculate the delay for a given retry attempt using exponential backoff.
 * Attempt 1: initialDelay
 * Attempt 2: initialDelay * backoffMultiplier
 * Attempt 3: initialDelay * backoffMultiplier^2
 * etc.
 */
function getRetryDelay(attempt: number, config: Required<RetryConfig>): number {
  const delay = config.initialDelay * Math.pow(config.backoffMultiplier, attempt - 1);
  return Math.min(delay, config.maxDelay);
}

/**
 * Wrap an async function with retry logic using exponential backoff.
 *
 * @param fn - The async function to execute
 * @param config - Retry configuration options
 * @param shouldRetry - Optional custom function to determine if error is retryable
 * @returns The result of the function if successful
 * @throws The last error if all retries fail
 *
 * @example
 * ```ts
 * const result = await withRetry(
 *   () => searchCards({ name: 'charizard' }),
 *   { maxRetries: 3 }
 * );
 * ```
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  config: RetryConfig = {},
  shouldRetry: (error: unknown) => boolean = isRetryableError
): Promise<T> {
  const finalConfig: Required<RetryConfig> = { ...DEFAULT_CONFIG, ...config };
  let lastError: unknown;

  for (let attempt = 0; attempt <= finalConfig.maxRetries; attempt++) {
    try {
      // First attempt (attempt === 0) runs immediately
      // Subsequent attempts wait with exponential backoff
      if (attempt > 0) {
        const delay = getRetryDelay(attempt, finalConfig);
        console.log(`[Retry] Attempt ${attempt + 1}/${finalConfig.maxRetries + 1} after ${delay}ms delay`);
        await sleep(delay);
      }

      return await fn();
    } catch (error) {
      lastError = error;

      // Check if we should retry
      if (!shouldRetry(error)) {
        console.log('[Retry] Error is not retryable, throwing immediately');
        throw error;
      }

      // Check if we have retries left
      if (attempt >= finalConfig.maxRetries) {
        console.log('[Retry] Max retries reached, throwing error');
        throw error;
      }

      console.log(`[Retry] Retryable error on attempt ${attempt + 1}:`, error);
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError;
}
